import { useCallback } from 'react';

import { ensureUsercentricsV3Loaded } from '@xing-com/cookie-consent-usercentrics';

import type { ContextualConsentEligibility } from '../use-contextual-consent-eligibility';
import { useContextualConsentEligibility } from '../use-contextual-consent-eligibility';
import {
  META_PIXEL_SERVICE_ID,
  hasContextualMeasurementOptOutCookie,
} from '../use-contextual-consent-eligibility/contextual-consent-eligibility';

const USERCENTRICS_TIMEOUT_MS = 6000;

const withTimeout = async <T>(promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Usercentrics consent update timed out')),
      USERCENTRICS_TIMEOUT_MS
    );
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

export type ContextualConsent = ContextualConsentEligibility & {
  updateConsent: () => Promise<void>;
};

export const useContextualConsent = (): ContextualConsent => {
  const eligibility = useContextualConsentEligibility();

  const updateConsent = useCallback(async (): Promise<void> => {
    if (
      !eligibility.isEligible ||
      typeof document === 'undefined' ||
      hasContextualMeasurementOptOutCookie(document.cookie)
    ) {
      return;
    }

    try {
      await withTimeout(
        ensureUsercentricsV3Loaded({ suppressCmpDisplay: true })
      );

      if (!window.__ucCmp) return;

      await withTimeout(
        window.__ucCmp.updateServicesConsents([
          { id: META_PIXEL_SERVICE_ID, consent: true },
        ])
      );
      await withTimeout(window.__ucCmp.saveConsents());
    } catch {
      // Contextual consent must never prevent an application from being sent.
    }
  }, [eligibility.isEligible]);

  return { ...eligibility, updateConsent };
};
